// src/pages/admin/SettingsPage.js
// Updated: 10/31/2025 05:40 PM EDT
// --- COPY-PASTE THIS ENTIRE FILE ---

import React, { useState, useEffect } from 'react';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../context/AuthContext';
// --- FIX: Added 'orderBy' to the import list ---
import { collection, query, where, onSnapshot, addDoc, setDoc, serverTimestamp, deleteDoc, doc, orderBy } from 'firebase/firestore';
// --- END OF FIX ---
import { UserPlus, Trash2, Link as LinkIcon, RefreshCw, Settings as SettingsIcon, Check, XCircle } from 'lucide-react'; // <-- 1. Import Check & XCircle

// Loading Spinner Component
const LoadingSpinner = () => (
    <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-dashed border-blue-500"></div>
    </div>
);

// Function to generate a simple, readable code
const generateJoinCode = (length = 6) => {
    const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
};

export default function AdminSettingsPage() {
    const { userData, currentUser } = useAuth();
    const [invites, setInvites] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState('');
    const [copiedCodeId, setCopiedCodeId] = useState(null); 

    // Listen for invites
    useEffect(() => {
        if (!userData?.orgId) return;
        setIsLoading(true);
        // --- 2. SORT BY CREATEDAT TO SHOW NEWEST FIRST ---
        const invitesQuery = query(
          collection(db, 'invitations'), 
          where('orgId', '==', userData.orgId),
          orderBy('createdAt', 'desc') // <-- This now works
        );
        // ------------------------------------------------
        const unsubscribe = onSnapshot(invitesQuery, (querySnapshot) => {
            const invitesData = [];
            querySnapshot.forEach((doc) => { invitesData.push({ id: doc.id, ...doc.data() }); });
            setInvites(invitesData);
            setIsLoading(false);
            setError('');
        }, (err) => {
            console.error('Error fetching invites in listener:', err);
            setError('Failed to load invitation list.');
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [userData?.orgId]);

    // Action to generate a new unique code
    const handleGenerateCode = async () => {
        if (isGenerating || !userData?.orgId) return;

        setIsGenerating(true);
        setError('');

        try {
            const newCode = generateJoinCode();
            
            const metadataRef = await addDoc(collection(db, 'invitations'), {
                orgId: userData.orgId,
                code: newCode,
                createdBy: currentUser.uid,
                createdAt: serverTimestamp(),
                usageCount: 0, // <-- 3. EXPLICITLY SET usageCount
                maxUses: 1,
            });
            
            await setDoc(doc(db, 'invitationCodes', newCode), {
                orgId: userData.orgId,
                inviteId: metadataRef.id,
            });
            
        } catch (err) {
            console.error('CRITICAL FAILURE: Invitation generation pipeline failed.', err);
            setError(`FAIL: Code generation blocked. Error: ${err.message}.`);
        } finally {
            setIsGenerating(false);
        }
    };

    // Action to revoke/delete a code
    const handleRevokeCode = async (inviteCode, inviteMetadataId) => {
        if (!window.confirm("Are you sure you want to revoke this invitation code?")) return;
        try {
            await deleteDoc(doc(db, 'invitationCodes', inviteCode));
            await deleteDoc(doc(db, 'invitations', inviteMetadataId));
        } catch (err) {
            console.error('Error revoking code:', err);
            setError('Failed to revoke code.');
        }
    };

    // Create the full public registration link
    const getJoinLink = (code) => {
        return `https://boards.multicustomsllc.com/register?join=${code}`;
    };

    // Handler for copying link
    const handleCopyLink = (code, id) => {
        if (copiedCodeId) return; 
        const joinLink = getJoinLink(code);
        navigator.clipboard.writeText(joinLink);
        setCopiedCodeId(id); 
        setTimeout(() => {
            setCopiedCodeId(null); 
        }, 2000);
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <div className="rounded-lg bg-white p-6 shadow-lg">
            <h1 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <SettingsIcon className="mr-3 h-7 w-7" /> Organization Settings
            </h1>

            <div className="border-b pb-4 mb-6">
                <h2 className="text-xl font-semibold mb-2">Organization ID</h2>
                <p className="text-sm text-gray-700">Your unique organization identifier for security purposes:</p>
                <code className="bg-gray-100 px-3 py-1 rounded text-sm font-mono mt-1 inline-block">{userData?.orgId || 'N/A'}</code>
            </div>

            <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <UserPlus className="mr-2 h-5 w-5" /> Invite Members
                </h2>
                <p className="text-sm text-gray-700 mb-4">
                    Generate a unique **join code** or a direct link for new board members to register and automatically join your organization. Codes are single-use.
                </p>

                <button
                    onClick={handleGenerateCode}
                    disabled={isGenerating}
                    className="flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                >
                    {isGenerating ? <RefreshCw className="mr-2 h-5 w-5 animate-spin" /> : <UserPlus className="mr-2 h-5 w-5" />}
                    Generate New Join Code
                </button>
            </div>

            {/* List of Active Codes */}
            <div>
                <h3 className="text-lg font-semibold mb-3">Invitation Codes ({invites.length})</h3>
                {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

                {invites.length === 0 ? (
                    <p className="text-gray-500">No invitation codes found.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Code</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Registration Link</th>
                                    {/* --- 4. UPDATE HEADER --- */}
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Usage</th>
                                    <th className="px-6 py-3"></th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {invites.map((invite) => {
                                    // --- 5. ADD 'isUsed' CHECK ---
                                    const isUsed = invite.usageCount >= invite.maxUses;
                                    
                                    return (
                                        <tr key={invite.id} className={isUsed ? 'bg-gray-100' : ''}>
                                            <td className={`px-6 py-4 whitespace-nowrap text-lg font-bold ${isUsed ? 'text-gray-400 line-through' : 'text-green-600'}`}>
                                                {invite.code}
                                            </td>
                                            
                                            <td 
                                                className="px-6 py-4 whitespace-nowrap text-sm"
                                                title={isUsed ? 'This link has already been used' : 'Click to copy link'}
                                            >
                                                {isUsed ? (
                                                    <span className="text-gray-500 font-medium flex items-center">
                                                        <XCircle className="h-4 w-4 mr-1 inline-block" />
                                                        Link Used
                                                    </span>
                                                ) : copiedCodeId === invite.id ? (
                                                    <span className="text-green-600 font-medium flex items-center cursor-pointer">
                                                        <Check className="h-4 w-4 mr-1 inline-block" />
                                                        Copied!
                                                    </span>
                                                ) : (
                                                    <span 
                                                        className="text-blue-600 hover:text-blue-800 flex items-center cursor-pointer"
                                                        onClick={() => handleCopyLink(invite.code, invite.id)}
                                                    >
                                                        <LinkIcon className="h-4 w-4 mr-1 inline-block" />
                                                        Copy Registration Link
                                                    </span>
                                                )}
                                            </td>
                                            
                                            {/* --- 6. UPDATE USAGE CELL --- */}
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-700">
                                                {invite.usageCount} / {invite.maxUses}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <button
                                                    onClick={() => handleRevokeCode(invite.code, invite.id)}
                                                    className="text-red-600 hover:text-red-900 flex items-center float-right"
                                                >
                                                    <Trash2 className="h-4 w-4 mr-1" /> Revoke
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}